import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, CalendarDays, MessageSquareWarning, MailWarning } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";
import { usePageMeta, PageHeader, Loader, ErrorState, Field, EmptyState, StatusBadge } from "@/components/common";
import { ROLE_LABEL, fmtDateTime } from "@/lib/format";
import { ImageField } from "@/components/ImageField";

function ProfileCard({ user, onSaved }) {
  const [form, setForm] = useState({ display_name: user.display_name || "", gamertag: user.gamertag || "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/auth/me", form);
      onSaved(data);
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="nx-card p-6 space-y-4" data-testid="profile-form">
      <div className="eyebrow">I tuoi dati</div>
      <div className="text-sm text-[#C6C6CE]/70">{user.email} · <span data-testid="profile-role" className="text-[#d6b3ff]">{ROLE_LABEL[user.role]}</span></div>
      <Field label="Nome visualizzato"><input data-testid="profile-name-input" className="nx-input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></Field>
      <Field label="Gamertag"><input data-testid="profile-gamertag-input" className="nx-input" value={form.gamertag} onChange={(e) => setForm({ ...form, gamertag: e.target.value })} /></Field>
      <button data-testid="profile-save-button" className="btn-primary btn-sm">Salva</button>
    </form>
  );
}

const SOC = ["discord", "instagram", "youtube", "twitch", "x", "tiktok"];

function LeaderClan({ clan, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    setForm({ description: clan.description || "", logo_url: clan.logo_url || "", vice_leader_name: clan.vice_leader_name || "", socials: { ...Object.fromEntries(SOC.map((k) => [k, ""])), ...(clan.socials || {}) }, membersText: (clan.members || []).map((m) => `${m.gamertag}|${m.role}`).join("\n") });
  }, [clan]);
  if (!form) return null;
  const save = async (e) => {
    e.preventDefault();
    const payload = { description: form.description, logo_url: form.logo_url || null, vice_leader_name: form.vice_leader_name, socials: form.socials };
    if (clan.members_managed_by_leader) {
      payload.members = form.membersText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [g, r] = l.split("|"); return { gamertag: g.trim(), role: (r || "membro").trim() }; });
    }
    try {
      const { data } = await api.put("/me/clan", payload);
      onSaved(data);
      toast.success("Clan aggiornato");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="nx-card p-6 space-y-4" data-testid="leader-clan-form">
      <div className="flex items-center justify-between"><div className="eyebrow">Gestisci il tuo clan</div><Link to={`/clan/${clan.slug}`} className="text-xs text-[#962FFF] hover:underline">{clan.name} [{clan.tag}]</Link></div>
      <Field label="Descrizione"><textarea data-testid="leader-clan-description" rows={4} className="nx-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <ImageField label="Logo del clan" testId="leader-clan-logo" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} />
        <Field label="Vice leader"><input data-testid="leader-clan-vice" className="nx-input" value={form.vice_leader_name} onChange={(e) => setForm({ ...form, vice_leader_name: e.target.value })} /></Field>
        {SOC.map((k) => <Field key={k} label={k}><input data-testid={`leader-clan-social-${k}`} className="nx-input" value={form.socials[k]} onChange={(e) => setForm({ ...form, socials: { ...form.socials, [k]: e.target.value } })} /></Field>)}
      </div>
      {clan.members_managed_by_leader ? (
        <Field label="Membri (uno per riga: gamertag|ruolo)"><textarea data-testid="leader-clan-members" rows={5} className="nx-input font-num text-xs" value={form.membersText} onChange={(e) => setForm({ ...form, membersText: e.target.value })} /></Field>
      ) : <p className="text-xs text-[#C6C6CE]/50">La gestione dei membri è riservata allo staff per questo clan.</p>}
      <button data-testid="leader-clan-save" className="btn-primary btn-sm">Salva clan</button>
    </form>
  );
}

function CorrectionForm({ events, onSent }) {
  const [form, setForm] = useState({ event_id: "", message: "" });
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post("/corrections", form);
      setForm({ event_id: "", message: "" });
      toast.success("Richiesta di correzione inviata");
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={send} className="nx-card p-6 space-y-4" data-testid="correction-form">
      <div className="eyebrow">Richiesta di correzione risultati</div>
      <Field label="Evento"><select data-testid="correction-event-select" required className="nx-input" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })}><option value="">Seleziona</option>{events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}</select></Field>
      <Field label="Descrizione (min. 10 caratteri)"><textarea data-testid="correction-message-input" required minLength={10} rows={3} className="nx-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></Field>
      <button data-testid="correction-submit" className="btn-primary btn-sm">Invia richiesta</button>
    </form>
  );
}

function VerifyBanner() {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Email di conferma inviata");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="verify-email-banner" className="nx-card border-amber-400/30 p-5 flex flex-wrap items-center gap-4 mb-6">
      <MailWarning className="w-5 h-5 text-amber-300" />
      <div className="flex-1 min-w-[220px] text-sm text-[#C6C6CE]">Conferma il tuo indirizzo email: ti abbiamo inviato un link. Fino alla conferma non puoi inviare candidature o richieste di correzione.</div>
      <button data-testid="resend-verification-button" disabled={busy} onClick={resend} className="btn-ghost btn-sm">{busy ? "Invio..." : "Invia di nuovo"}</button>
    </div>
  );
}

export default function Area() {
  usePageMeta("Area personale");
  const { user, setUser } = useAuth();
  const { data, loading, error, reload } = useFetch(user ? "/me/overview" : null);
  const apps = useFetch(user ? "/me/clan-applications" : null);
  if (user === false) return <Navigate to="/login" replace />;
  if (!user || (loading && !data)) return <div className="pt-24"><Loader /></div>;
  if (error) return <div className="max-w-3xl mx-auto px-5 pt-32"><ErrorState message={error} onRetry={reload} /></div>;
  const events = [...new Map(data.participations.map((p) => [p.event.id, p.event])).values()];
  return (
    <div data-testid="area-page">
      <PageHeader eyebrow="Area personale" title={`Ciao, ${user.display_name}`} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10">{user.email_verified === false && <VerifyBanner />}</div>
      <div className="max-w-7xl mx-auto px-5 md:px-8 pb-10 grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <ProfileCard user={user} onSaved={(u) => setUser({ ...user, ...u })} />
          <div className="nx-card p-6" data-testid="my-clan-card">
            <div className="eyebrow mb-3">Il tuo clan</div>
            {(data.clan || data.led_clan) ? <Link to={`/clan/${(data.clan || data.led_clan).slug}`} className="flex items-center gap-2 text-[#F5F5F7] hover:text-[#962FFF]"><Shield className="w-4 h-4" />{(data.clan || data.led_clan).name} [{(data.clan || data.led_clan).tag}]</Link> : <p className="text-sm text-[#C6C6CE]/50">Nessun clan associato. L'associazione viene gestita dallo staff.</p>}
          </div>
        </div>
        <div className="lg:col-span-2 space-y-6">
          {data.led_clan && <LeaderClan clan={data.led_clan} onSaved={reload} />}
          {(apps.data || []).length > 0 && (
            <div className="nx-card p-6" data-testid="my-applications"><div className="eyebrow mb-3">Le tue candidature clan</div>
              <ul className="space-y-3 text-sm">{apps.data.map((a) => <li key={a.id} className="border-b border-[#C6C6CE]/5 pb-2"><div className="flex justify-between gap-3"><span className="text-[#F5F5F7]">{a.clan_name} [{a.tag}]</span><span data-testid={`my-application-status-${a.id}`} className="text-xs uppercase tracking-wider text-[#d6b3ff]">{a.status.replace("_", " ")}</span></div><div className="text-xs text-[#C6C6CE]/50 mt-1">Inviata {fmtDateTime(a.created_at)}</div>{a.response && <div className="text-xs text-emerald-300 mt-1">Risposta: {a.response}</div>}</li>)}</ul>
            </div>
          )}
          <div>
            <h2 className="text-lg font-bold uppercase text-[#F5F5F7] mb-3">Le tue partecipazioni</h2>
            {data.participations.length ? (
              <div className="nx-card divide-y divide-[#C6C6CE]/5" data-testid="participations-list">
                {data.participations.map((p, i) => (
                  <Link key={i} to={`/eventi/${p.event.slug}`} className="flex items-center gap-4 p-4 hover:bg-[#17171D] transition-colors duration-200">
                    <CalendarDays className="w-4 h-4 text-[#962FFF]" />
                    <div className="flex-1 min-w-0"><div className="text-[#F5F5F7] truncate">{p.event.title}</div><div className="text-xs text-[#C6C6CE]/50">{p.team_name} · {fmtDateTime(p.event.start_at)}</div></div>
                    <StatusBadge status={p.event.status} />
                  </Link>
                ))}
              </div>
            ) : <EmptyState icon={CalendarDays} title="Nessuna partecipazione" text="Le partecipazioni appaiono quando il tuo clan o il tuo gamertag è iscritto a un evento." />}
          </div>
          {["clan_leader", "staff", "super_admin"].includes(user.role) && (
            <>
              <CorrectionForm events={events} onSent={reload} />
              <div className="nx-card p-6" data-testid="my-corrections"><div className="eyebrow mb-3">Le tue richieste</div>
                {data.corrections.length ? <ul className="space-y-3 text-sm">{data.corrections.map((c) => <li key={c.id} className="border-b border-[#C6C6CE]/5 pb-2"><div className="flex justify-between gap-3"><span className="text-[#F5F5F7]">{c.event_title}</span><span className="text-xs uppercase tracking-wider text-[#d6b3ff]">{c.status}</span></div><div className="text-xs text-[#C6C6CE]/60 mt-1">{c.message}</div>{c.response && <div className="text-xs text-emerald-300 mt-1">Risposta: {c.response}</div>}</li>)}</ul>
                  : <div className="flex items-center gap-2 text-sm text-[#C6C6CE]/50"><MessageSquareWarning className="w-4 h-4" /> Nessuna richiesta inviata.</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
